import React, { useState, useEffect } from 'react';
import { Clock, Plus, Trash2, Edit2, Save, X, User, Settings } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { toastMessage } from '@/utils/toast-message';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const DAY_LABELS: Record<string, string> = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday' };
const PERIODS = [1, 2, 3, 4, 5, 6, 7, 8];
const BASE = '/api/parent-app/admin';

interface PaClass { id: number; class_name: string; academic_year_id: number; curriculum_class_id: number | null; section_id: number | null; }
interface CurriculumClass { id: number; class_label: string; }
interface CurriculumGroup { curriculum_id: number; curriculum_name: string; classes: CurriculumClass[]; }
interface SectionItem { id: number; section_label: string; }
interface Teacher { id: number; name: string; }
interface Subject { id: number; name: string; code?: string | null; }
interface TimetableEntry {
  id?: number;
  class_id: number;
  day_of_week: string;
  period_number: number;
  subject: string;
  subject_id: number | null;
  teacher_id: number | null;
  start_time: string;
  end_time: string;
  room: string;
  teacher?: { id: number; name: string };
}

interface EditingCell { day: string; period: number; entry: Partial<TimetableEntry> | null; }
interface PeriodTiming { period_number: number; start_time: string; end_time: string; }

export default function TimetablePage() {
  const [classes, setClasses] = useState<PaClass[]>([]);
  const [classGroups, setClassGroups] = useState<CurriculumGroup[]>([]);
  const [selectedCurriculumClassId, setSelectedCurriculumClassId] = useState<number | ''>('');
  const [selectedSectionId, setSelectedSectionId] = useState<number | ''>('');
  const [sections, setSections] = useState<SectionItem[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [timetable, setTimetable] = useState<TimetableEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<EditingCell | null>(null);
  const [saving, setSaving] = useState(false);
  const [periodTimings, setPeriodTimings] = useState<PeriodTiming[]>([]);
  const [showTimingsModal, setShowTimingsModal] = useState(false);
  const [timingDraft, setTimingDraft] = useState<PeriodTiming[]>([]);
  const [savingTimings, setSavingTimings] = useState(false);

  useEffect(() => {
    Promise.all([
      apiRequest<any>(`${BASE}/pa-classes`),
      apiRequest<any>(`${BASE}/classes`),
      apiRequest<any>(`${BASE}/teachers`),
      apiRequest<any>(`${BASE}/period-timings`),
    ]).then(([clsRes, grpRes, tchRes, ptRes]) => {
      const clsList = clsRes?.data?.data ?? clsRes?.data ?? [];
      setClasses(Array.isArray(clsList) ? clsList : []);
      setClassGroups(grpRes?.data?.grouped ?? []);
      const tchList = tchRes?.data?.rows ?? tchRes?.data?.data ?? tchRes?.data ?? [];
      setTeachers(Array.isArray(tchList) ? tchList : []);
      const ptList: PeriodTiming[] = Array.isArray(ptRes?.data) ? ptRes.data : [];
      setPeriodTimings(ptList);
    });
  }, []);

  // When curriculum class changes — fetch sections, auto-resolve if only one
  useEffect(() => {
    setSelectedSectionId('');
    setSections([]);
    setSelectedClass(null);
    setEditing(null);
    if (!selectedCurriculumClassId) return;
    apiRequest<any>(`${BASE}/classes/${selectedCurriculumClassId}/sections`).then(res => {
      const list: SectionItem[] = res?.data?.data ?? [];
      setSections(Array.isArray(list) ? list : []);
      if (!list.length) {
        // No sections — resolve directly
        const paClass = classes.find(c => c.curriculum_class_id === Number(selectedCurriculumClassId));
        if (paClass) setSelectedClass(paClass.id);
      } else if (list.length === 1) {
        // Single section — auto-select
        setSelectedSectionId(list[0].id);
      }
    });
  }, [selectedCurriculumClassId]);

  // When section changes — resolve to pa_classes.id
  useEffect(() => {
    if (!selectedCurriculumClassId || !selectedSectionId) return;
    const paClass = classes.find(
      c => c.curriculum_class_id === Number(selectedCurriculumClassId) && c.section_id === Number(selectedSectionId)
    );
    if (paClass) { setSelectedClass(paClass.id); setEditing(null); }
  }, [selectedSectionId]);

  useEffect(() => {
    if (!selectedClass) { setSubjects([]); return; }
    setLoading(true);
    Promise.all([
      apiRequest<any>(`${BASE}/timetable?class_id=${selectedClass}`),
      apiRequest<any>(`${BASE}/timetable/subjects?class_id=${selectedClass}`),
    ]).then(([ttRes, subRes]) => {
      setTimetable(ttRes?.data ?? []);
      setSubjects(subRes?.data ?? []);
    }).finally(() => setLoading(false));
  }, [selectedClass]);

  const getEntry = (day: string, period: number) =>
    timetable.find(e => e.day_of_week === day && e.period_number === period);

  const startEdit = (day: string, period: number) => {
    const existing = getEntry(day, period);
    const periodEntry = timetable.find(e => e.period_number === period && e.start_time);
    if (existing) {
      const matchedId = existing.subject_id
        ?? subjects.find(s => s.name.toLowerCase() === (existing.subject || '').toLowerCase())?.id
        ?? null;
      setEditing({ day, period, entry: { ...existing, subject_id: matchedId } });
    } else {
      setEditing({ day, period, entry: { day_of_week: day, period_number: period, subject: '', subject_id: null, teacher_id: null, start_time: periodEntry?.start_time ?? '', end_time: periodEntry?.end_time ?? '', room: '' } });
    }
  };


  const saveEntry = async () => {
    if (!editing || !selectedClass) return;
    setSaving(true);
    try {
      const existing = getEntry(editing.day, editing.period);
      const payload = { ...editing.entry, class_id: selectedClass, day_of_week: editing.day, period_number: editing.period };
      let res;
      if (existing?.id) {
        res = await apiRequest<any>(`${BASE}/timetable/${existing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        res = await apiRequest<any>(`${BASE}/timetable`, { method: 'POST', body: JSON.stringify(payload) });
      }
      if (res?.success) {
        setTimetable(prev => {
          const filtered = prev.filter(e => !(e.day_of_week === editing.day && e.period_number === editing.period));
          const teacher = teachers.find(t => t.id === payload.teacher_id);
          return [...filtered, { ...res.data, teacher: teacher || null }];
        });
        toastMessage('Saved', 'success');
        setEditing(null);
      } else {
        toastMessage(res?.message ?? 'Failed to save', 'error');
      }
    } catch { toastMessage('Failed to save', 'error'); }
    finally { setSaving(false); }
  };

  const deleteEntry = async (entry: TimetableEntry) => {
    if (!entry.id) return;
    await apiRequest<any>(`${BASE}/timetable/${entry.id}`, { method: 'DELETE' });
    setTimetable(prev => prev.filter(e => e.id !== entry.id));
    toastMessage('Deleted', 'success');
  };

  const openTimingsModal = () => {
    const draft = PERIODS.map(p => {
      const existing = periodTimings.find(t => t.period_number === p);
      return { period_number: p, start_time: existing?.start_time?.slice(0, 5) ?? '', end_time: existing?.end_time?.slice(0, 5) ?? '' };
    });
    // Add break slots: 10 = Lunch Break, 11 = Snack Break
    [10, 11].forEach(bp => {
      const existing = periodTimings.find(t => t.period_number === bp);
      draft.push({ period_number: bp, start_time: existing?.start_time?.slice(0, 5) ?? '', end_time: existing?.end_time?.slice(0, 5) ?? '' });
    });
    setTimingDraft(draft);
    setShowTimingsModal(true);
  };

  const saveTimings = async () => {
    setSavingTimings(true);
    try {
      const filled = timingDraft.filter(t => t.start_time && t.end_time);
      if (!filled.length) { toastMessage('Enter at least one period timing', 'error'); return; }
      const res = await apiRequest<any>(`${BASE}/period-timings`, { method: 'POST', body: JSON.stringify({ timings: filled }) });
      if (res?.success) {
        setPeriodTimings(Array.isArray(res.data) ? res.data : []);
        setShowTimingsModal(false);
        toastMessage('Period timings saved', 'success');
      }
    } catch { toastMessage('Failed to save', 'error'); }
    finally { setSavingTimings(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: 'var(--bg)' }}>
      {/* Header */}
      <div className="page-head">
        <div className="page-head-icon">
          <Clock size={24} />
        </div>
        <div className="page-head-text">
          <h1>Timetable</h1>
          <p>Manage class schedules</p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-sm" onClick={openTimingsModal} style={{ background: '#111', color: '#fff', border: 'none', gap: 6 }}>
            <Settings size={14} /> Period Timings
          </button>
          <select
            value={selectedCurriculumClassId}
            onChange={e => setSelectedCurriculumClassId(Number(e.target.value) || '')}
            className="select"
            style={{ width: 180 }}
          >
            <option value="">Select Class</option>
            {classGroups.map(g => {
              const filtered = g.classes.filter(gc => classes.some(c => c.curriculum_class_id === gc.id));
              if (!filtered.length) return null;
              return (
                <optgroup key={g.curriculum_id} label={g.curriculum_name}>
                  {filtered.map(gc => <option key={gc.id} value={gc.id}>{gc.class_label}</option>)}
                </optgroup>
              );
            })}
          </select>
          {sections.length > 0 && (
            <select
              value={selectedSectionId}
              onChange={e => setSelectedSectionId(Number(e.target.value) || '')}
              className="select"
              style={{ width: 140 }}
            >
              <option value="">Select Section</option>
              {sections.map(s => <option key={s.id} value={s.id}>{s.section_label}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="page-body" style={{ display: 'flex', flexDirection: 'column' }}>
        {!selectedClass ? (
          <div className="empty">
            <div className="empty-art">
              <Clock size={44} />
            </div>
            <h3>Select a class</h3>
            <p>Choose a class from the dropdown to view and manage its timetable</p>
          </div>
        ) : loading ? (
          <div className="empty">
            <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid var(--border)', borderTopColor: 'var(--slate)', animation: 'spin 0.8s linear infinite' }} />
            <p className="muted" style={{ marginTop: 12 }}>Loading timetable...</p>
          </div>
        ) : (
          <div className="card" style={{ overflow: 'auto' }}>
            <table className="tbl tbl-sticky" style={{ minWidth: 1200, tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Period</th>
                  {DAYS.map(d => (
                    <th key={d}>{DAY_LABELS[d]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERIODS.map((period) => {
                  const timing = periodTimings.find(t => t.period_number === period);
                  return (
                  <tr key={period}>
                    <td style={{ verticalAlign: 'top', padding: '14px 16px' }}>
                      <div style={{ fontWeight: 800, fontSize: 13 }}>P{period}</div>
                      {timing ? (
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Clock size={10} />
                          {timing.start_time.slice(0, 5)}–{timing.end_time.slice(0, 5)}
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3 }}>—</div>
                      )}
                    </td>
                    {DAYS.map(day => {
                      const entry = getEntry(day, period);
                      const isEditing = editing?.day === day && editing?.period === period;
                      return (
                        <td key={day} style={{ padding: 6 }}>
                          {isEditing ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 160, padding: 8 }}>
                              {subjects.length > 0 ? (
                                <select
                                  autoFocus
                                  value={editing.entry?.subject_id != null ? String(editing.entry.subject_id) : ''}
                                  onChange={e => {
                                    const sub = subjects.find(s => s.id === Number(e.target.value));
                                    setEditing(p => p ? { ...p, entry: { ...p.entry, subject_id: Number(e.target.value) || null, subject: sub?.name ?? '' } } : p);
                                  }}
                                  className="select"
                                  style={{ fontSize: 12, height: 36 }}
                                >
                                  <option value="">Select Subject</option>
                                  {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                              ) : (
                                <input
                                  autoFocus
                                  placeholder="Subject"
                                  value={editing.entry?.subject ?? ''}
                                  onChange={e => setEditing(p => p ? { ...p, entry: { ...p.entry, subject: e.target.value } } : p)}
                                  className="input"
                                  style={{ fontSize: 12, height: 36 }}
                                />
                              )}
                              <select
                                value={editing.entry?.teacher_id ?? ''}
                                onChange={e => setEditing(p => p ? { ...p, entry: { ...p.entry, teacher_id: Number(e.target.value) || null } } : p)}
                                className="select"
                                style={{ fontSize: 12, height: 36 }}
                              >
                                <option value="">No teacher</option>
                                {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                              </select>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button onClick={saveEntry} disabled={saving} className="btn btn-primary btn-sm" style={{ flex: 1 }}>
                                  <Save size={14} />{saving ? 'Saving...' : 'Save'}
                                </button>
                                <button onClick={() => setEditing(null)} className="btn btn-soft btn-sm">
                                  <X size={14} />
                                </button>
                              </div>
                            </div>
                          ) : entry ? (
                            <div
                              style={{
                                position: 'relative',
                                padding: 10,
                                borderRadius: 'var(--r-sm)',
                                border: '1px solid var(--border)',
                                minHeight: 64,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                backgroundColor: 'var(--surface-2)'
                              } as React.CSSProperties}
                              onMouseEnter={e => {
                                const actions = (e.currentTarget as HTMLElement).querySelector('[data-actions]') as HTMLElement;
                                if (actions) actions.style.display = 'flex';
                              }}
                              onMouseLeave={e => {
                                const actions = (e.currentTarget as HTMLElement).querySelector('[data-actions]') as HTMLElement;
                                if (actions) actions.style.display = 'none';
                              }}
                            >
                              <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--text)' }}>{entry.subject}</div>
                              {entry.teacher && (
                                <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <User size={11} />
                                  {entry.teacher.name}
                                </div>
                              )}
                              <div
                                data-actions
                                style={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  display: 'none',
                                  gap: 4,
                                  zIndex: 10
                                }}
                              >
                                <button
                                  onClick={() => startEdit(day, period)}
                                  className="icon-btn"
                                  style={{ width: 28, height: 28 }}
                                  title="Edit"
                                >
                                  <Edit2 size={13} />
                                </button>
                                <button
                                  onClick={() => deleteEntry(entry)}
                                  className="icon-btn"
                                  style={{ width: 28, height: 28, color: 'var(--danger)' }}
                                  title="Delete"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEdit(day, period)}
                              style={{
                                width: '100%',
                                minHeight: 64,
                                border: '1.5px dashed var(--border-2)',
                                borderRadius: 'var(--r-sm)',
                                background: 'transparent',
                                color: 'var(--text-3)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.14s'
                              }}
                              onMouseEnter={e => {
                                (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-deep)';
                                (e.currentTarget as HTMLElement).style.color = 'var(--accent-deep)';
                              }}
                              onMouseLeave={e => {
                                (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-2)';
                                (e.currentTarget as HTMLElement).style.color = 'var(--text-3)';
                              }}
                            >
                              <Plus size={18} />
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Period Timings Modal */}
      {showTimingsModal && (
        <>
          <div className="scrim" onClick={() => setShowTimingsModal(false)}></div>
          <div className="modal" style={{ width: 420 }}>
            <div className="modal-head" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <h2>Period Timings</h2>
                <p>Set school-wide timings for all periods</p>
              </div>
              <button className="icon-btn" style={{ marginLeft: 'auto' }} onClick={() => setShowTimingsModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ padding: 0, overflowY: 'auto', maxHeight: '60vh' }}>
              <table className="tbl tbl-sticky" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>Period</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                  </tr>
                </thead>
                <tbody>
                  {timingDraft.filter(r => r.period_number <= 8).map((row, i) => (
                    <tr key={row.period_number}>
                      <td style={{ fontWeight: 700 }}>P{row.period_number}</td>
                      <td>
                        <input
                          type="time"
                          value={row.start_time}
                          onChange={e => setTimingDraft(prev => prev.map((r, idx) => idx === i ? { ...r, start_time: e.target.value } : r))}
                          className="input"
                          style={{ fontSize: 13, height: 34, width: '100%' }}
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          value={row.end_time}
                          onChange={e => setTimingDraft(prev => prev.map((r, idx) => idx === i ? { ...r, end_time: e.target.value } : r))}
                          className="input"
                          style={{ fontSize: 13, height: 34, width: '100%' }}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={3} style={{ padding: '10px 12px 4px', fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: 'none' }}>
                      Breaks
                    </td>
                  </tr>
                  {[{ period_number: 10, label: 'Lunch Break' }, { period_number: 11, label: 'Snack Break (optional)' }].map(({ period_number, label }) => {
                    const idx = timingDraft.findIndex(r => r.period_number === period_number);
                    const row = timingDraft[idx];
                    if (!row) return null;
                    return (
                      <tr key={period_number}>
                        <td style={{ fontWeight: 600, fontSize: 12 }}>{label}</td>
                        <td>
                          <input
                            type="time"
                            value={row.start_time}
                            onChange={e => setTimingDraft(prev => prev.map((r, i) => i === idx ? { ...r, start_time: e.target.value } : r))}
                            className="input"
                            style={{ fontSize: 13, height: 34, width: '100%' }}
                          />
                        </td>
                        <td>
                          <input
                            type="time"
                            value={row.end_time}
                            onChange={e => setTimingDraft(prev => prev.map((r, i) => i === idx ? { ...r, end_time: e.target.value } : r))}
                            className="input"
                            style={{ fontSize: 13, height: 34, width: '100%' }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setShowTimingsModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveTimings} disabled={savingTimings}>
                <Save size={14} />{savingTimings ? 'Saving...' : 'Save All'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
